/**
 * AdminBrandDetail — `/admin/brands/:brandId`. Tabs:
 *  • Info    — edit brand details + publish toggle
 *  • Events  — list & assign/unassign events to this brand
 *  • Plans   — create + manage membership plans (passport / subscription)
 *  • Members — roster + MRR
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Loader2, ArrowLeft, Save, ExternalLink, Plus, Trash2, Calendar, Users,
  Ticket, DollarSign, Tag, X, Globe, EyeOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Info tab
// ============================================================
function InfoTab({ brand, onSaved }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState({
    name: brand.name || '',
    tagline: brand.tagline || '',
    description: brand.description || '',
    hero_image_url: brand.hero_image_url || '',
    logo_url: brand.logo_url || '',
    accent_color: brand.accent_color || '#8b5cf6',
    city: brand.city || '',
    kind: brand.kind || 'series',
    is_published: !!brand.is_published,
    perks: brand.perks || [],
  });
  const [newPerk, setNewPerk] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/brands/${brand.brand_id}`, form, { headers });
      toast.success('Brand updated');
      onSaved(res.data.brand);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addPerk = () => {
    if (!newPerk.trim()) return;
    setForm({ ...form, perks: [...form.perks, newPerk.trim()] });
    setNewPerk('');
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brand-edit-name" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Kind</Label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="brand-edit-kind"
            >
              <option value="series">Recurring Series</option>
              <option value="club">Membership Club</option>
              <option value="program">Program</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="brand-edit-city" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Tagline</Label>
          <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="brand-edit-tagline" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Description</Label>
          <Textarea
            rows={5}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="brand-edit-description"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Hero image URL</Label>
            <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} data-testid="brand-edit-hero" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Logo image URL</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} data-testid="brand-edit-logo" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Accent color (hex)</Label>
            <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} data-testid="brand-edit-accent" />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                data-testid="brand-edit-publish"
              />
              <span className="text-foreground">Publish public landing page</span>
            </label>
          </div>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Member perks (bullet list)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              placeholder="e.g. Free coat check"
              value={newPerk}
              onChange={(e) => setNewPerk(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPerk())}
              data-testid="brand-perk-new"
            />
            <Button variant="outline" onClick={addPerk} data-testid="brand-perk-add"><Plus className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="mt-2 space-y-1">
            {form.perks.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`brand-perk-${i}`}>
                <Tag className="h-3 w-3 text-muted-foreground" />
                <span className="text-foreground/80 flex-1">{p}</span>
                <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, perks: form.perks.filter((_, j) => j !== i) })}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="brand-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Events tab — list + assign
// ============================================================
function EventsTab({ brand, onChanged }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tagged, setTagged] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch ONLY the current user's events (backend auto-scopes by
      // org when an auth header is present). Without the headers the
      // backend treats the call as anonymous and returns every public
      // event, which is why other organizers' events were showing up
      // in the "Available events" list.
      const res = await axios.get(
        `${API}/hosted-events?status=all&include_drafts=true&limit=200`,
        { headers },
      );
      const all = res.data?.events || [];
      const t = []; const a = [];
      for (const ev of all) {
        if (ev.brand_id === brand.brand_id) t.push(ev);
        else if (!ev.brand_id) a.push(ev);
      }
      setTagged(t);
      setAvailable(a);
    } catch {
      setTagged([]); setAvailable([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.brand_id]);

  useEffect(() => { load(); }, [load]);

  const assign = async (eventId) => {
    try {
      await axios.post(`${API}/brands/${brand.brand_id}/assign-event`, { event_id: eventId }, { headers });
      toast.success('Event assigned');
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assign failed');
    }
  };

  const unassign = async (eventId) => {
    try {
      await axios.post(`${API}/brands/${brand.brand_id}/unassign-event`, { event_id: eventId }, { headers });
      toast.success('Event removed from brand');
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unassign failed');
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="space-y-5">
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <h3 className="font-semibold text-foreground mb-3">In this brand ({tagged.length})</h3>
          {tagged.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events assigned yet.</p>
          ) : (
            <div className="space-y-2">
              {tagged.map((ev) => (
                <div key={ev.event_id} className="flex items-center gap-3 p-2 rounded-md border border-border" data-testid={`brand-event-${ev.event_id}`}>
                  <Calendar className="h-4 w-4 text-indigo-300" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{ev.name}</div>
                    <div className="text-xs text-muted-foreground">{ev.event_date}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => unassign(ev.event_id)} data-testid={`brand-event-remove-${ev.event_id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5 text-rose-400" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardContent className="p-5">
          <h3 className="font-semibold text-foreground mb-3">Available events ({available.length})</h3>
          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground">All your events are either assigned to a brand or none exist yet.</p>
          ) : (
            <div className="space-y-2">
              {available.map((ev) => (
                <div key={ev.event_id} className="flex items-center gap-3 p-2 rounded-md border border-border">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{ev.name}</div>
                    <div className="text-xs text-muted-foreground">{ev.event_date}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => assign(ev.event_id)} data-testid={`brand-event-assign-${ev.event_id}`}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add to brand
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Plans tab — list + create
// ============================================================
function PlansTab({ brand }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', pricing_model: 'passport', price: '', billing_interval: 'month',
    description: '', event_limit: '', free_admission: true, ticket_discount_percent: '',
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/brands/${brand.brand_id}/plans`, { headers });
      setPlans(res.data?.plans || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.brand_id]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.price || parseFloat(form.price) <= 0) {
      toast.error('Name and a price > 0 are required'); return;
    }
    setCreating(true);
    try {
      const benefits = {
        free_admission: !!form.free_admission,
      };
      if (form.ticket_discount_percent) benefits.ticket_discount_percent = parseInt(form.ticket_discount_percent, 10);
      await axios.post(`${API}/brands/${brand.brand_id}/plans`, {
        name: form.name.trim(),
        pricing_model: form.pricing_model,
        price: parseFloat(form.price),
        billing_interval: form.pricing_model === 'subscription' ? form.billing_interval : undefined,
        description: form.description.trim() || undefined,
        event_limit: form.pricing_model === 'passport' && form.event_limit ? parseInt(form.event_limit, 10) : undefined,
        benefits,
      }, { headers });
      toast.success('Plan created');
      setShowForm(false);
      setForm({ name: '', pricing_model: 'passport', price: '', billing_interval: 'month',
                description: '', event_limit: '', free_admission: true, ticket_discount_percent: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (planId) => {
    if (!window.confirm('Deactivate this plan? Existing members keep their access.')) return;
    try {
      await axios.delete(`${API}/brands/${brand.brand_id}/plans/${planId}`, { headers });
      toast.success('Plan removed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="plan-new-btn">
          <Plus className="h-4 w-4 mr-2" /> New plan
        </Button>
      </div>

      {showForm && (
        <Card className="bg-card border-border">
          <CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-foreground">New membership plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Plan name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Gold Member" data-testid="plan-name" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Type</Label>
                <select
                  value={form.pricing_model}
                  onChange={(e) => setForm({ ...form, pricing_model: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="plan-pricing-model"
                >
                  <option value="passport">Passport (one-time)</option>
                  <option value="subscription">Subscription (recurring)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Price (USD)</Label>
                <Input type="number" min="1" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="299" data-testid="plan-price" />
              </div>
              {form.pricing_model === 'subscription' && (
                <div>
                  <Label className="text-xs text-muted-foreground">Billed</Label>
                  <select
                    value={form.billing_interval}
                    onChange={(e) => setForm({ ...form, billing_interval: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="plan-billing-interval"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                </div>
              )}
              {form.pricing_model === 'passport' && (
                <div>
                  <Label className="text-xs text-muted-foreground">Event limit <span className="opacity-60">(blank = unlimited)</span></Label>
                  <Input type="number" min="1" value={form.event_limit} onChange={(e) => setForm({ ...form, event_limit: e.target.value })} placeholder="12" data-testid="plan-event-limit" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="plan-description" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Benefits</Label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={form.free_admission}
                  onChange={(e) => setForm({ ...form, free_admission: e.target.checked })}
                  data-testid="plan-free-admission"
                />
                <span className="text-foreground">Free admission to brand events</span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">Ticket discount %</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  className="w-24"
                  value={form.ticket_discount_percent}
                  onChange={(e) => setForm({ ...form, ticket_discount_percent: e.target.value })}
                  data-testid="plan-discount-percent"
                />
                <span className="text-xs text-muted-foreground">(0 for none)</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowForm(false)} data-testid="plan-create-cancel">Cancel</Button>
              <Button onClick={create} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="plan-create-save">
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {plans.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No plans yet. Create a passport or subscription so fans can join.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="plans-grid">
          {plans.map((p) => (
            <Card key={p.plan_id} className="bg-card border-border" data-testid={`plan-row-${p.plan_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-foreground">{p.name}</h4>
                    <div className="text-xs text-muted-foreground capitalize mt-0.5">
                      {p.pricing_model}{p.pricing_model === 'subscription' ? ` · ${p.billing_interval}ly` : (p.event_limit ? ` · ${p.event_limit} events` : ' · unlimited')}
                    </div>
                  </div>
                  <div className="text-xl font-bold text-foreground shrink-0">
                    ${p.price}{p.pricing_model === 'subscription' && <span className="text-xs text-muted-foreground">/{p.billing_interval}</span>}
                  </div>
                </div>
                {p.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <Badge variant="outline" className="text-[10px]">
                    {p.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  {p.benefits?.free_admission && <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-700/40">Free admission</Badge>}
                  {p.benefits?.ticket_discount_percent > 0 && <Badge variant="outline" className="text-[10px]">{p.benefits.ticket_discount_percent}% off</Badge>}
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => remove(p.plan_id)} data-testid={`plan-delete-${p.plan_id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Members tab
// ============================================================
function MembersTab({ brand }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState({ members: [], count: 0, mrr_usd: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/brands/${brand.brand_id}/members`, { headers });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ members: [], count: 0, mrr_usd: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.brand_id]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Active members</div>
            <div className="text-2xl font-bold text-foreground mt-1" data-testid="members-count">{data.count}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Estimated MRR</div>
            <div className="text-2xl font-bold text-emerald-300 mt-1" data-testid="members-mrr">${data.mrr_usd}</div>
          </CardContent>
        </Card>
      </div>
      <Card className="bg-card border-border">
        <CardContent className="p-5">
          {data.members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet — share your brand page once it's published.</p>
          ) : (
            <div className="divide-y divide-border">
              {data.members.map((m) => (
                <div key={m.membership_id} className="py-2.5 flex items-center gap-3" data-testid={`member-row-${m.membership_id}`}>
                  <Users className="h-4 w-4 text-indigo-300" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{m.buyer_name || m.buyer_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.buyer_email} · {m.pricing_model}{m.billing_interval ? ` · ${m.billing_interval}ly` : ''}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize">{m.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Main page
// ============================================================
export default function AdminBrandDetail() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/brands/me`, { headers });
      const found = (res.data?.brands || []).find((b) => b.brand_id === brandId);
      if (!found) {
        toast.error('Brand not found');
        navigate('/admin/brands');
        return;
      }
      setBrand(found);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load brand');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !brand) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-indigo-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="admin-brand-detail">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-5">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/brands')} data-testid="back-to-brands">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground truncate" data-testid="brand-title">{brand.name}</h1>
              {brand.is_published ? (
                <Badge variant="outline" className="text-emerald-300 border-emerald-700/40 bg-emerald-500/10">
                  <Globe className="h-3 w-3 mr-1" /> Live
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground border-border bg-muted/30">
                  <EyeOff className="h-3 w-3 mr-1" /> Draft
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">/series/{brand.slug}</p>
          </div>
          {brand.is_published && (
            <Link to={`/series/${brand.slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" data-testid="open-public-page">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View public page
              </Button>
            </Link>
          )}
        </div>

        <Tabs defaultValue="info" data-testid="brand-detail-tabs">
          <TabsList>
            <TabsTrigger value="info" data-testid="tab-info">Info</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="mt-4"><InfoTab brand={brand} onSaved={setBrand} /></TabsContent>
          <TabsContent value="events" className="mt-4"><EventsTab brand={brand} onChanged={load} /></TabsContent>
          <TabsContent value="plans" className="mt-4"><PlansTab brand={brand} /></TabsContent>
          <TabsContent value="members" className="mt-4"><MembersTab brand={brand} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
